<template>
  <v-data-table-server
    v-model:options="pagination"
    :headers="headers"
    :items="requests"
    :page="pagination.page"
    :items-length="total"
    :loading="loading"
    class="elevation-1 nowrap-headers"
    :items-per-page="pagination.itemsPerPage"
    :items-per-page-options="[
      { value: 5, title: '5' },
      { value: 10, title: '10' },
      { value: 25, title: '25' },
      { value: 50, title: '50' },
    ]"
    items-per-page-text="Rows per page"
    @update:options="updatePagination"
  >
    <template #progress>
      <v-progress-linear indeterminate color="red" />
    </template>
    <template #item="{ item, index }">
      <tr
        :class="{ tableRowColorSelected: index === activeRow }"
        class="tableRowColor"
        @click="activeRow = index"
      >
        <!--ID-->
        <td>
          <span>
            {{ item._id.slice(item._id.length - 4, item._id.length) }}
          </span>
        </td>
        <!--STATUS-->
        <td>
          <v-row align-center justify-center>
            <v-chip
              label
              outlined
              :color="index === activeRow ? 'white' : getStatus(item).color"
            >
              <span> {{ getStatus(item).message }} </span>
            </v-chip>
          </v-row>
        </td>
        <!--USER-->
        <td>
          <span v-if="item.user.details">{{ item.user.details.name }} </span>
          <span v-if="item.user.details">{{ item.user.details.surname }} </span>
        </td>
        <!--REQUESTS-->
        <td>
          {{ item.requests.length }}
          {{
            item.requests.length === 1
              ? $t("part_requests.headers._part")
              : $t("part_requests.headers._parts")
          }}
        </td>
        <!--INSTITUTIONS-->
        <td>
          <v-row align-center>
            <v-chip
              v-if="item.assignedInstitutions.length > 0"
              :color="item.color.rgb"
              :dark="item.color.dark"
            >
              {{ item.assignedInstitutions | institutions }}
            </v-chip>
            <span v-if="item.assignedInstitutions.length > 1">
              (+{{ item.assignedInstitutions.length - 1 }}
              {{
                item.assignedInstitutions.length - 1 === 1
                  ? $t("part_requests.headers._other")
                  : $t("part_requests.headers._others")
              }})
            </span>
          </v-row>
        </td>
        <!--CREATION DATE-->
        <td>
          <div class="text-center text-truncate pb-2">
            {{ formatDate(item.dateCreated) }}
          </div>
        </td>
        <!--EXPIRATION DATE-->
        <td>
          <span v-if="getStatus(item).status === 1">
            {{ expirationDate(item.dateCreated) }}
          </span>
          <span v-else> - </span>
        </td>
        <!--ACTIONS-->
        <td>
          <v-row align-center justify-center>
            <!--VIEW DETAILS-->
            <v-btn icon @click="viewRequestDetails(item)">
              <v-icon color="grey-darken-2"> view_list </v-icon>
            </v-btn>
            <!--VIEW OFFERS-->
            <v-btn
              icon
              @click="
                viewOffers(
                  item._id,
                  item.assignedInstitutions,
                  getStatus(item).status === 1 ? true : false,
                )
              "
            >
              <v-icon color="grey-darken-2"> local_offer </v-icon>
            </v-btn>
            <!--ASSIGN INSTITUTIONS-->
            <v-btn
              v-if="
                ($store.getters.role === 'LekoAdmin' ||
                  $store.getters.role === 'LekoEmployee' ||
                  $store.getters.role === 'SuperAdmin') &&
                getStatus(item).status === 1
              "
              icon
              @click="assignInstitutions(item._id, item.assignedInstitutions)"
            >
              <v-icon color="grey-darken-2"> assignment </v-icon>
            </v-btn>
            <!--REJECT REQUEST-->
            <v-btn
              v-if="getStatus(item).status === 1"
              icon
              :loading="loadingReject"
              @click="rejectPartRequest(item._id)"
            >
              <v-icon color="grey-darken-2"> remove_circle_outline </v-icon>
            </v-btn>
            <!--DELETE REQUEST-->
            <v-btn
              v-if="
                ($store.getters.role === 'LekoAdmin' ||
                  $store.getters.role === 'LekoEmployee' ||
                  $store.getters.role === 'SuperAdmin') &&
                getStatus(item).status === 1
              "
              icon
              :loading="loadingDelete"
              @click="deletePartRequest(item._id)"
            >
              <v-icon color="grey-darken-2"> delete </v-icon>
            </v-btn>
            <!--RETURN REQUEST-->
            <v-btn
              v-if="getStatus(item).status === 2"
              icon
              @click="openReturnDialog(item)"
            >
              <v-icon color="grey-darken-2"> archive </v-icon>
            </v-btn>
          </v-row>
        </td>
      </tr>
      <tr v-if="requests && requests.length < 1">
        <td :colspan="headers.length" class="text-center pa-4">
          <v-alert type="info" border="left" color="red lighten-2" text>
            No data available.
          </v-alert>
        </td>
      </tr>
    </template>
  </v-data-table-server>
</template>

<script>
import moment from "moment";
import store from "../../store";

import getRequest from "../../api/getRequest";
import deleteRequest from "../../api/deleteRequest";
import putRequest from "../../api/putRequest";

function generateRandomColor() {
  const random = () => Math.floor(Math.random() * Math.floor(255));
  const color = { r: random(), g: random(), b: random() };
  const brightness = (color.r * 299 + color.g * 587 + color.b * 114) / 1000;
  return {
    rgb: `rgb(${color.r},${color.g},${color.b})`,
    dark: brightness < 155,
  };
}

export default {
  filters: {
    moment(date) {
      moment.locale(localStorage.getItem("language"));
      return moment(date).format("LLL");
    },
    institutions(value) {
      if (value.length > 0) {
        const index = value.findIndex(
          (v) => v.id._id.toString() === store.getters.institutionId.toString(),
        );
        if (index > -1) return value[index].id.title;
        return value[0].id.title;
      }
      return "None";
    },
  },
  props: ["selected"],
  data: () => ({
    loading: false,
    activeRow: null,
    requests: [],
    total: 0,
    pagination: {
      itemsPerPage: 5,
      page: 1,
      groupBy: ["dateCreated"],
      sortBy: [{ key: "dateCreated", order: "desc" }],
      sortDesc: [true],
    },
    headers: [
      { title: "ID", sortable: false, key: "_id" },
      { title: "Status", sortable: false, key: "status" },
      { title: "User", sortable: false, key: "user" },
      { title: "Parts", sortable: false, key: "parts" },
      { title: "Institutions", sortable: false, key: "institutions" },
      { title: "Date Created", sortable: true, key: "dateCreated" },
      { title: "Expiration Date", sortable: false, key: "expiration_date" },
      { title: "Actions", sortable: false, key: "actions" },
    ],
    loadingInstitutions: false,
    loadingDelete: false,
    loadingReject: false,
    institutions: [],
    selectedInstitutions: [],
  }),
  watch: {
    "$props.selected": {
      handler() {
        this.getPartRequests();
      },
    },
    pagination: {
      handler() {
        this.getPartRequests();
      },
      deep: true,
    },
    requests: {
      handler() {
        this.requests.forEach((request) => {
          request.color = generateRandomColor();
        });
      },
      deep: true,
    },
  },
  created() {
    this.getInstitutions();
    this.getLocale();
  },
  methods: {
    getLocale() {
      this.headers[0].title = this.$t("part_requests.headers.id");
      this.headers[1].title = this.$t("part_requests.headers.status");
      this.headers[2].title = this.$t("part_requests.headers.user");
      this.headers[3].title = this.$t("part_requests.headers.parts");
      this.headers[4].title = this.$t(
        "part_requests.headers.assigned_institutions",
      );
      this.headers[5].title = this.$t("part_requests.headers.date_created");
      this.headers[6].title = this.$t("part_requests.headers.expiration_date");
      this.headers[7].title = this.$t("part_requests.headers.actions");
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
    updatePagination(options) {
      this.pagination = {
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 5,
        groupBy:
          options.groupBy && options.groupBy.length
            ? options.groupBy
            : ["dateCreated"],
        sortBy:
          options.sortBy && options.sortBy.length
            ? options.sortBy
            : { key: "dateCreated", order: "desc" },
        sortDesc:
          options.sortDesc && options.sortDesc.length
            ? options.sortDesc
            : [true],
      };
      this.activeRow = null;
    },
    getPartRequests() {
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "dateCreated",
        order: "desc",
      };
      getRequest(
        "/part-request",
        `category=1&status=${this.$props.selected}&institution_id=${
          this.$store.getters.institutionId
        }&role=${this.$store.getters.role}&limit=${
          this.pagination.itemsPerPage
        }&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${sort.order === "desc"}`,
      )
        .then((response) => {
          this.requests = response.partRequests;
          this.total = response.count;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    deletePartRequest(id) {
      // eslint-disable-next-line no-alert, no-restricted-globals
      const c = confirm(this.$t("confirm_alert"));
      if (c === true) {
        this.loadingDelete = true;
        deleteRequest("/part-request", `id=${id}`)
          .then(() => {
            this.getPartRequests();
          })
          .catch(() => {})
          .finally(() => {
            this.loadingDelete = false;
          });
      }
    },
    viewRequestDetails(data) {
      this.$emit("view-details", data);
    },
    viewOffers(id, assignedInstitutions, editable) {
      this.$emit("view-offers", { id, assignedInstitutions, editable });
    },
    getInstitutions() {
      this.loadingInstitutions = true;
      getRequest("/institution")
        .then((response) => {
          this.institutions = response.institutions;
          this.loadingInstitutions = false;
        })
        .catch(() => {
          this.loadingInstitutions = false;
        });
    },
    assignInstitutions(id, institutions) {
      this.$emit("assign-institutions", { id, institutions });
    },
    rejectPartRequest(id) {
      // eslint-disable-next-line no-alert, no-restricted-globals
      const c = confirm(this.$t("confirm_alert"));
      if (c === true) {
        this.loadingReject = true;
        putRequest("/part-request/reject", `id=${id}`, {
          institution: this.$store.getters.institutionId,
        })
          .then(() => {
            this.getPartRequests();
          })
          .catch(() => {})
          .finally(() => {
            this.loadingReject = false;
          });
      }
    },
    openReturnDialog(item) {
      this.$emit("open-return-dialog", item);
    },

    getStatus(item) {
      /*
       * Status:
       * 1 - Waiting or an offer
       * 2 - Waiting for return confirmation.
       */
      if (
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "LekoEmployee" ||
        this.$store.getters.role === "SuperAdmin"
      ) {
        if (item.status === 1)
          return {
            status: 1,
            color: "orange",
            message: this.$t("part_requests.status.waiting_offer"),
          };
        // status 3 will be removed in the future
        if (item.status === 3.2)
          return {
            status: 2,
            color: "red",
            message: this.$t("part_requests.status.pending_return"),
          };
      } else if (
        this.$store.getters.role === "InstitutionAdmin" ||
        this.$store.getters.role === "InstitutionManager" ||
        this.$store.getters.role === "User" ||
        this.$store.getters.role === "EbayOperator"
      ) {
        const institution = item.assignedInstitutions.find(
          (i) =>
            i.id._id.toString() ===
            this.$store.getters.institutionId.toString(),
        ); // eslint-disable-line max-len
        if (institution && !institution.offer.proposed)
          return {
            status: 1,
            color: "orange",
            message: this.$t("part_requests.status.waiting_offer"),
          };
        if (institution && institution.offer.wantToReturn)
          return {
            status: 2,
            color: "red",
            message: this.$t("part_requests.status.pending_return"),
          };
      }
      return { color: "white", message: "" };
    },
    expirationDate(date) {
      const creationTime = new Date(date).getTime();
      const expirationTime = creationTime + 259200000; // 72 hours after creation.
      const expiringDate = new Date(expirationTime);
      moment.locale(store.getters.language);
      return moment(expiringDate).fromNow();
    },
  },
};
</script>
